import React, { useEffect, useState } from 'react';
import type { CrewJobChecklist, ProjectCrewNote } from '../../types/crewSlack';
import {
  subscribeActiveCrewChecklist,
  subscribeProjectCrewNotes,
  updateCrewChecklistItems,
} from '../../utils/crewSlackFirestore';
import './CrewSlackHubPanel.css';

type Props = {
  projectId: string;
  projectName?: string;
};

const CrewSlackHubPanel: React.FC<Props> = ({ projectId, projectName }) => {
  const [notes, setNotes] = useState<ProjectCrewNote[]>([]);
  const [checklist, setChecklist] = useState<CrewJobChecklist | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setError(null);
    const unsubNotes = subscribeProjectCrewNotes(
      projectId,
      setNotes,
      err => setError(err.message),
    );
    const unsubChecklist = subscribeActiveCrewChecklist(
      projectId,
      setChecklist,
      err => setError(err.message),
    );
    return () => {
      unsubNotes();
      unsubChecklist();
    };
  }, [projectId]);

  const toggleItem = async (itemId: string) => {
    if (!checklist) return;
    setSaving(true);
    try {
      const items = checklist.items.map(i =>
        i.id === itemId ? { ...i, done: !i.done } : i,
      );
      await updateCrewChecklistItems(checklist.id, items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="crew-slack-hub">
      <header className="crew-slack-hub__header">
        <h3>Crew Slack</h3>
        <p>
          Notes, damaged-tool logs, and pre-departure checklist for{' '}
          {projectName || 'this job'} (from the crew channel bot).
        </p>
      </header>
      {error && <div className="crew-slack-hub__error">{error}</div>}

      <section className="crew-slack-hub__section">
        <h4>Pre-departure checklist</h4>
        {!checklist ? (
          <p className="crew-slack-hub__empty">
            No active checklist yet. Crew can create one from Slack (&quot;checklist&quot;), or it will appear after the bot seeds it.
          </p>
        ) : (
          <>
            {!!checklist.recommendations?.length && (
              <ul className="crew-slack-hub__recs">
                {checklist.recommendations.map(r => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            )}
            <ul className="crew-slack-hub__checklist">
              {checklist.items.map(item => (
                <li key={item.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={item.done === true}
                      disabled={saving}
                      onChange={() => toggleItem(item.id)}
                    />
                    <span>
                      {item.title}
                      {item.required ? ' (required)' : ''}
                      {item.recommendation ? ` — ${item.recommendation}` : ''}
                      {item.doneByName ? ` · ${item.doneByName}` : ''}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="crew-slack-hub__section">
        <h4>Crew notes</h4>
        {!notes.length ? (
          <p className="crew-slack-hub__empty">No crew notes yet.</p>
        ) : (
          <ul className="crew-slack-hub__notes">
            {notes.map(n => (
              <li key={n.id}>
                <div className="crew-slack-hub__note-meta">
                  <strong>{n.authorName}</strong>
                  <span>{n.createdAt.toLocaleString()}</span>
                  {n.kind && n.kind !== 'note' ? <em>{n.kind.replace('_', ' ')}</em> : null}
                </div>
                <div className="crew-slack-hub__note-text">{n.text}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default CrewSlackHubPanel;
